import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { requestPasswordReset, isValidEmail } from '../../services/authService';

/**
 * ResetPasswordRequestForm Component
 * Handles password reset email request
 * Implements security best practices to prevent user enumeration
 */
const ResetPasswordRequestForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useUntypedTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Component state
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  /**
   * Handles form submission for password reset request
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Reset states
    setError(null);
    setSuccess(false);

    // Validate email format
    if (!email.trim()) {
      setError(t('auth.errors.invalidEmail'));
      return;
    }

    if (!isValidEmail(email)) {
      setError(t('auth.errors.invalidEmail'));
      return;
    }

    setLoading(true);

    try {
      // Request password reset email
      await requestPasswordReset(email);

      // Show generic success message (doesn't reveal if email exists)
      setSuccess(true);
      setEmail(''); // Clear email field

      console.log('✅ Password reset email request completed');
    } catch (error: any) {
      console.error('❌ Password reset request failed:', error);

      // Handle different error types
      if (error.message === 'INVALID_EMAIL') {
        setError(t('auth.errors.invalidEmail'));
      } else if (error.message === 'TOO_MANY_REQUESTS') {
        setError('Too many requests. Please try again later.');
      } else {
        setError(t('auth.errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Navigates back to sign-in page
   */
  const handleBackToSignIn = () => {
    navigate('/signin');
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: isMobile ? 2 : 4,
        maxWidth: 500,
        width: '100%',
        textAlign: 'center',
        borderRadius: { xs: 2, sm: 3 },
        backgroundColor: 'background.paper',
      }}
    >
      {/* Header */}
      <Typography
        variant="h4"
        sx={{
          mb: 2,
          color: 'primary.main',
        }}
      >
        {t('auth.forgotPassword.title')}
      </Typography>

      {/* Success Message */}
      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 2.5,
            textAlign: 'left',
          }}
        >
          {t('auth.forgotPassword.successMessage')}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2.5,
            textAlign: 'left',
          }}
        >
          {error}
        </Alert>
      )}

      {/* Instructions */}
      {!success && (
        <Typography
          variant="body2"
          sx={{
            mb: 3,
            textAlign: 'left',
            color: 'text.secondary',
          }}
        >
          {t('auth.forgotPassword.instructions')}
        </Typography>
      )}

      {/* Form */}
      {!success && (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mb: 3,
          }}
        >
          <TextField
            type="email"
            label={t('auth.forgotPassword.emailLabel')}
            variant="outlined"
            required
            fullWidth
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={loading}
            autoFocus
            autoComplete="email"
          />

          <Button variant="contained" type="submit" fullWidth disabled={loading}>
            {loading ? (
              <CircularProgress
                size={24}
                sx={{
                  color: 'white',
                }}
              />
            ) : (
              t('auth.forgotPassword.submitButton')
            )}
          </Button>
        </Box>
      )}

      {/* Back to Sign In Link */}
      <MuiLink
        component="button"
        type="button"
        variant="body2"
        onClick={handleBackToSignIn}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          textDecoration: 'none',
          color: 'primary.main',
          cursor: 'pointer',
          fontSize: '0.875rem',
          '&:hover': {
            textDecoration: 'underline',
          },
          border: 'none',
          background: 'none',
          padding: 0,
          font: 'inherit',
        }}
      >
        <ArrowBackIcon fontSize="small" />
        {t('auth.forgotPassword.backToSignIn')}
      </MuiLink>
    </Paper>
  );
};

export default ResetPasswordRequestForm;
